/** Resolved street coverage. Metres in the existing region tangent frame.
 * Geometry is data-only; bucket indices are disposable derived acceleration.
 * Height/graph authority remains with the existing road beds. */
export const STREET_SURFACE_VERSION = 1;
export interface StreetPoint { x:number; y:number; z:number }
export interface StreetPath { id:string; width:number; points:StreetPoint[] }
export interface StreetArm { x:number; z:number; half:number }
export interface StreetJunction { id:string; x:number; z:number; radius:number; arms:StreetArm[] }
export interface StreetSurfacePlan { version:1; paths:StreetPath[]; junctions:StreetJunction[] }
const length=(x:number,z:number)=>Math.sqrt(x*x+z*z);
const mix=(a:StreetPoint,b:StreetPoint,t:number):StreetPoint=>({x:a.x+(b.x-a.x)*t,y:a.y+(b.y-a.y)*t,z:a.z+(b.z-a.z)*t});
export function roundStreetPath(points:StreetPoint[],radius=2.8,spacing=.5):StreetPoint[]{
  if(points.length<2||points.length>200000||!points.every(p=>[p.x,p.y,p.z].every(Number.isFinite)))throw new Error('Invalid street controls');
  if(!Number.isFinite(radius)||radius<0||!Number.isFinite(spacing)||spacing<=0)throw new Error('Invalid curve radius or spacing');
  const closed=length(points[0].x-points.at(-1)!.x,points[0].z-points.at(-1)!.z)<.001;
  const controls=closed?points.slice(0,-1):points,out:StreetPoint[]=[];
  const line=(p:StreetPoint)=>{if(!out.length){out.push({...p});return;}const a=out.at(-1)!,n=Math.max(1,Math.ceil(length(p.x-a.x,p.z-a.z)/spacing));if(out.length+n>200000)throw new Error('Street sampling budget exceeded');for(let j=1;j<=n;j++)out.push(mix(a,p,j/n));};
  for(let i=0;i<controls.length;i++){
    const p=controls[i],a=controls[(i+controls.length-1)%controls.length],b=controls[(i+1)%controls.length];
    if(!closed&&(i===0||i===controls.length-1)){line(p);continue;}
    const la=length(p.x-a.x,p.z-a.z),lb=length(b.x-p.x,b.z-p.z),dot=((p.x-a.x)*(b.x-p.x)+(p.z-a.z)*(b.z-p.z))/(la*lb||1);
    if(dot>.999||la<.001||lb<.001){line(p);continue;}
    const cut=Math.min(radius,la*.4,lb*.4),start=mix(p,a,cut/la),end=mix(p,b,cut/lb);line(start);
    const n=Math.max(6,Math.ceil(cut*3));if(out.length+n>200000)throw new Error('Street sampling budget exceeded');for(let j=1;j<=n;j++){const t=j/n;out.push(mix(mix(start,p,t),mix(p,end,t),t));}
  }
  if(closed)line(out[0]);
  return out.filter((p,i)=>i===0||length(p.x-out[i-1].x,p.z-out[i-1].z)>.0001);
}
interface Segment { a:StreetPoint; b:StreetPoint; half:number; path:string }
function distance(x:number,z:number,a:StreetPoint,b:StreetPoint){
  const dx=b.x-a.x,dz=b.z-a.z,t=Math.max(0,Math.min(1,((x-a.x)*dx+(z-a.z)*dz)/(dx*dx+dz*dz||1)));
  return length(x-a.x-dx*t,z-a.z-dz*t);
}
/** Polynomial union rounds the INSIDE of a junction, not just its centerline.
 * Pairwise evaluation is followed by min, so arm order cannot affect coverage. */
export function junctionDistance(j:StreetJunction,x:number,z:number):number {
  const d=length(x-j.x,z-j.z);if(d>j.radius)return Infinity;
  let result=Infinity;
  for(let a=0;a<j.arms.length;a++)for(let b=a+1;b<j.arms.length;b++){
    const u=j.arms[a],v=j.arms[b],dot=u.x*v.x+u.z*v.z;
    if(dot<-.98||dot>.98)continue;
    const start={x:j.x,y:0,z:j.z};
    const da=distance(x,z,start,{x:j.x+u.x*j.radius,y:0,z:j.z+u.z*j.radius})-u.half;
    const db=distance(x,z,start,{x:j.x+v.x*j.radius,y:0,z:j.z+v.z*j.radius})-v.half;
    const k=Math.min(u.half,v.half)*1.5*(1-Math.min(1,d/j.radius)*Math.min(1,d/j.radius));
    const h=Math.max(0,k-Math.abs(da-db))/Math.max(k,.00001);
    result=Math.min(result,Math.min(da,db)-h*h*k*.25);
  }
  return result;
}
/** Local grid: queries and endpoint attachment avoid all-street scans. */
export class StreetSurfaceIndex {
  private segments:Segment[]=[];
  private grid=new Map<string,number[]>();
  private joins=new Map<string,number[]>();
  constructor(readonly plan:StreetSurfacePlan){
    for(const p of plan.paths)for(let i=1;i<p.points.length;i++){
      const s={a:p.points[i-1],b:p.points[i],half:p.width/2,path:p.id},id=this.segments.push(s)-1;
      this.insert(this.grid,id,Math.min(s.a.x,s.b.x)-s.half-2,Math.max(s.a.x,s.b.x)+s.half+2,Math.min(s.a.z,s.b.z)-s.half-2,Math.max(s.a.z,s.b.z)+s.half+2);
    }
    for(const [i,j] of plan.junctions.entries())this.insert(this.joins,i,j.x-j.radius,j.x+j.radius,j.z-j.radius,j.z+j.radius);
  }
  private key(x:number,z:number){return `${Math.floor(x/8)},${Math.floor(z/8)}`;}
  private insert(grid:Map<string,number[]>,id:number,x0:number,x1:number,z0:number,z1:number){
    for(let z=Math.floor(z0/8);z<=Math.floor(z1/8);z++)for(let x=Math.floor(x0/8);x<=Math.floor(x1/8);x++){
      const key=`${x},${z}`,ids=grid.get(key);if(ids)ids.push(id);else grid.set(key,[id]);
    }
  }
  nearby(x:number,z:number){return (this.grid.get(this.key(x,z))??[]).map(i=>this.segments[i]);}
  sample(x:number,z:number):number {
    const key=this.key(x,z);let d=8;
    for(const id of this.grid.get(key)??[]){const s=this.segments[id];d=Math.min(d,distance(x,z,s.a,s.b)-s.half);}
    for(const id of this.joins.get(key)??[])d=Math.min(d,junctionDistance(this.plan.junctions[id],x,z));
    return Math.max(-8,d);
  }
}
export function buildStreetSurface(paths:StreetPath[]):StreetSurfacePlan {
  const ids=new Set<string>();let count=0;
  for(const p of paths){
    if(!p.id||ids.has(p.id)||!Number.isFinite(p.width)||p.width<=0||p.width>64||p.points.length<2)throw new Error(`Invalid street ${p.id}`);
    ids.add(p.id);count+=p.points.length;
    if(count>200000||!p.points.every(q=>[q.x,q.y,q.z].every(Number.isFinite)))throw new Error('Street complexity or coordinates invalid');
  }
  const plan:StreetSurfacePlan={version:1,paths,junctions:[]},index=new StreetSurfaceIndex(plan),seen=new Set<string>();
  for(const p of paths)for(const q of [p.points[0],p.points.at(-1)!]){
    const id=`${Math.round(q.x*1000)},${Math.round(q.z*1000)}`;if(seen.has(id))continue;seen.add(id);
    const arms:StreetArm[]=[];
    for(const s of index.nearby(q.x,q.z)){
      if(distance(q.x,q.z,s.a,s.b)>.025)continue;
      for(const end of [s.a,s.b]){
        const dx=end.x-q.x,dz=end.z-q.z,l=length(dx,dz);if(l<.025)continue;
        const arm={x:dx/l,z:dz/l,half:s.half},same=arms.find(a=>a.x*arm.x+a.z*arm.z>.999);
        if(same)same.half=Math.max(same.half,arm.half);else arms.push(arm);
      }
    }
    if(arms.length>1)plan.junctions.push({id,x:q.x,z:q.z,radius:Math.max(...arms.map(a=>a.half))*3+1,arms});
  }
  return plan;
}
